import { ApiV1Response, http } from 'api/v1/http'
import { TradeType } from 'store/market/types'
import { OrderDirection } from 'store/spot/types'
import {
  Address,
  Ether,
  L2ChainId,
  L2TokenId,
  Price,
  SymbolId,
  SymbolName,
  Timestamp,
  TokenSymbol,
} from 'types'

export interface SpotSymbol {
  id: SymbolId // 100103
  name: SymbolName // 'BTC_USDT'
  startTime: Timestamp // 1680278400000
  endTime: Timestamp // 5995814400000
  trade: 'SPOTS' // 'SPOTS'
  // The difference key name will cause the project to be difficult to use, so we add the 'baseCurrency' to the SpotSymbol.
  // baseName: TokenSymbol // 'BTC',
  baseCurrency: TokenSymbol // 'BTC'
  baseMinimumIncrement: number // 0.01
  baseScale: number // 2
  baseMinimumQuantity: number // 0.01
  baseMaximumQuantity: number // 10000.0
  // The difference key name will cause the project to be difficult to use, so we add the 'quoteCurrency' to the SpotSymbol.
  // quoteName: TokenSymbol // 'USDT'
  quoteCurrency: TokenSymbol // 'USDT'
  quoteMinimumIncrement: number // 0.5
  quoteScale: number // 1
  supportMarginTrade: boolean // true
  alwaysChargeQuote: boolean // true
  l2BaseCurrencyId: number
  l2QuoteCurrencyId: number
}

export interface PerpSymbol {
  id: SymbolId // 145
  layer2PerpId?: SymbolId // 155 from token mapping list, use to sign the layer2 transaction
  name: SymbolName // 'XETH'
  startTime: Timestamp // 1680278400000
  endTime: Timestamp // 5995814400000
  trade: 'CONTRACTS' // 'CONTRACTS'
  type: string // 'PERPETUAL'
  baseCurrency: TokenSymbol // 'ETH'
  marginCurrency: TokenSymbol // 'USDT'
  quoteCurrency: TokenSymbol // 'USDT'
  multiplier: number // 0.1
  quoteMinimumIncrement: number // 0.5
  quoteScale: number // 1
  baseScale: number // 0
  baseMinimumIncrement: number // 1
  maximumQuantityPerOrder: number // 100
  riskLimit: {
    id: number // 106
    initialMarginRate: number // 0.02
    maintenanceMarginRate: number // 0.01
    marginRateStep: number // 0.01
    maxLeverage: number // 5
    riskLimitBase: number // 200
    riskLimitStep: number // 100
    maxRiskLimitSteps: number // 9
    createdAt: Timestamp // 1546956031080
  }
  settlementFeeRate: number // 0.0
  referencedIndexes: {
    SPOT: number // 10135
    FAIR: number // 10138
    PREMIUM_1M: number // 10139
    PREMIUM_AGGREGATE: number // 10140
    MAX_PREMIUM_RATE: number // 10137
    FUNDING_RATE_1M: number // 10141
    FUNDING_RATE_AGGREGATE: number // 10142
    LENDING_RATE_1D: number // 10136
  }
}

export interface CurrencyChainInfo {
  chainId: L2ChainId // 1 or 2 ... or 7
  address: Address // "0x0000000000000000000000000000000000000000"
  decimals: number // 18
  fastWithdraw: boolean // true
}
export interface Currency {
  scale(arg0: string, scale: any): unknown
  id: number // 100, zkex currency id
  name: TokenSymbol // "BTC", zkex currency name
  main: boolean // false, can it be used as a contract margin
  conversionRatio: number // 1.0, margin conversion ratio
  displayName: string // "Bitcoin", zkex currency name
  createdAt: Timestamp // 1680278400000
  tokenIconUrl: string // https://xxx.com/xx.svg
  l2CurrencyId: L2TokenId // 150, layer2 currency id
  l2Symbol: TokenSymbol
  valid: boolean // false will not be displayed in the UI
  chains: {
    [x: L2ChainId]: CurrencyChainInfo
  }
}

export enum CurrencyMappingTokenType {
  Spot,
  Perp,
}
export interface CurrencyMapping {
  id: number // 1, mapping id
  tokenId: number // 100, zkex currency id
  tokenName: TokenSymbol // "BTC", zkex currency name
  mappingTokenId: number // 150, layer2 currency id
  mappingTokenName: string
  tokenIconUrl: string // https://xxx.com/xx.svg, token url
  tokenType: CurrencyMappingTokenType // 0: Spot, 1: Perp
}

export interface IConvertToken {
  id: number
  name: TokenSymbol
  chainTokenId: number
  displayName: TokenSymbol
  displayOrder: number
  scale: number
  minTradeVolume: number
  maxTradeVolume: number
  iconUrl: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface MarketTrades {
  spotsSymbols: SpotSymbol[]
  contractsCurrencies: TokenSymbol[]
  contractsSymbols: PerpSymbol[]
  convertTokens: IConvertToken[]
  indexes: {
    id: number // 10109
    name: string // 'BCH', 'BCH_DAILYRATE'
    startTime: Timestamp // 0
    endTime: Timestamp // 5995814400000
    scale: number // 2
  }[]
  spotsCurrencies: TokenSymbol[]
  riskLimits: {
    id: number // 105
    initialMarginRate: number // 0.01
    maintenanceMarginRate: number // 0.005
    marginRateStep: number // 0.005
    maxLeverage: number // 10
    riskLimitBase: number // 200
    riskLimitStep: number // 100
    maxRiskLimitSteps: number // 9
    createdAt: Timestamp // 1546956010600
  }[]
  tokenMappingList: CurrencyMapping[]
  currencies: Currency[]
  chains: {
    [x: L2TokenId]: {
      [x: L2ChainId]: CurrencyChainInfo
    }
  }
}

export async function getMarketTrades<
  T extends ApiV1Response<MarketTrades>,
>(): Promise<T> {
  return await http.get(`/market/trades`).then((r) => r.data)
}

export type MarketIndexName = string // 'BTC', 'BTC_USD_PI8H', 'BTC_DAILYRATE' ...
export type MarketIndexPrice = Ether // 42000.0, 1.0E-4 ...
export type MarketIndex = [Timestamp, MarketIndexPrice]
export interface MarketIndexes {
  nextFundingTime: MarketIndex // [Timestamp]
  [x: MarketIndexName]: MarketIndex
}
export async function getMarketIndexes<
  T extends ApiV1Response<MarketIndexes>,
>(): Promise<T> {
  return await http.get(`/market/indexes`).then((r) => r.data)
}

export async function getMarketIndexesByName<
  T extends ApiV1Response<{
    results: MarketIndex[]
  }>,
>(name: MarketIndexName): Promise<T> {
  const r = await http.get(`/market/indexes/${name}`)
  return r.data
}

export interface MarketPrice {
  symbolId: SymbolId
  direction: OrderDirection
}
export interface MarketPriceResponse {
  SHORT: {
    [x: SymbolName]: string
  }
  LONG: {
    [x: SymbolName]: string
  }
}
export async function getMarketPrice<
  T extends ApiV1Response<MarketPriceResponse>,
>(tradeType: TradeType, list: MarketPrice[]): Promise<T> {
  const r = await http.post(`/trading/market/price/${tradeType}`, {
    marketPriceList: list,
  })
  return r.data
}

export interface MarketPriceConfigResponse {
  SPOTS_CHANGE_LIMIT: number // 0.1
  CONTRACTS_CHANGE_LIMIT: number // 0.1
  SPOTS_SIGN_PERCENT: number // 0.8
  CONTRACTS_SIGN_PERCENT: number // 0.8
}

export async function getMarketPriceConfig<
  T extends ApiV1Response<MarketPriceConfigResponse>,
>(): Promise<T> {
  return await http.get(`/market/market/price/config`).then((r) => r.data)
}

export type Resolution =
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR4'
  | 'HOUR'
  | 'MIN30'
  | 'MIN15'
  | 'MIN5'
  | 'MIN'

export type BarDataItem = [
  Timestamp, // 1685404800000 time
  Price, // 4000 open
  Price, // 4000 high
  Price, // 4000 low
  Price, // 4000 close
  number, // 0.3 volume
  number, // 12000
]
export interface GetMarketBarData {
  results: BarDataItem[]
}
export interface GetMarketBarParams {
  symbol: SymbolName | TokenSymbol
  type: Resolution
}
export async function getMarketBar<T extends ApiV1Response<GetMarketBarData>>(
  params: GetMarketBarParams,
  query?: {
    start?: number
    end?: number
  },
): Promise<T> {
  return await http
    .get(`/market/bars/${params.symbol}/${params.type}`, {
      params: query,
    })
    .then((r) => r.data)
}

export type TickerTuple = [
  number, // timestamp
  number, // open
  number, // high
  number, // low
  number, // close
  number, // volume
  number, // value
] // timestamp, open, high, low, close, volume, value
export async function getAllTicker<
  T extends ApiV1Response<Record<SymbolName, TickerTuple>>,
>() {
  return await http
    .get<T>(`/market/allTicker`)
    .then((r) => r.data)
    .then((r) => r.data)
}
